import {
  ChangeDetectionStrategy,
  Component,
  AfterViewInit,
  Input,
  OnDestroy,
  signal,
  inject,
  output,
  EventEmitter,
  Output,
  input,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as atlas from 'azure-maps-control';
import { HttpClientModule } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { RouteService } from './formly-fields/services/route.service';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-route-map',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, HttpClientModule, NgClass],
  template: `
    <div class="relative rounded-md" data-cy="route-map">
      <div [id]="mapId" class="w-full border rounded-md" [ngClass]="height"></div>

      @if (routeInfo()) {
        <div class="absolute top-2 right-2 p-2 rounded text-xs bg-white">
          <div class="text-dark">Afstand: {{ routeInfo()?.distance }} km</div>
        </div>
      }

      <div class="absolute bottom-2 right-2 flex gap-2">
        @if (_locations().length > 2) {
          <!--          <button-->
          <!--            mat-mini-fab-->
          <!--            color="primary"-->
          <!--            type="button"-->
          <!--            (click)="toggleOptimizeRoute()"-->
          <!--            matTooltip="Route optimaliseren">-->
          <!--            <mat-icon>shuffle</mat-icon>-->
          <!--          </button>-->
        }
      </div>

      @if (loading()) {
        <div class="absolute top-2 left-2">
          <div class="bg-white p-2 rounded">
            <span class="text-dark">Route berekenen...</span>
          </div>
        </div>
      }

      @if (selectionModeActive() && selectionModeActiveEnabled) {
        <div class="absolute bottom-2 left-2">
          <div class="bg-white p-2 rounded text-sm">
            <span class="text-dark">Klik binnen de straal om de locatie te verplaatsen</span>
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        margin: 1rem 0;
      }

      ::ng-deep .maplibregl-canvas-container canvas {
        border-radius: 0.375rem; /* Rounded md */
      }
      ::ng-deep {
        #atlas-map-shortcuts,
        #atlas-map-state,
        .atlas-control-container,
        #atlas-map-style {
          display: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteMapComponent implements AfterViewInit, OnDestroy {
  readonly mapId = `routeMap-${crypto.randomUUID()}`;
  @Input() set locations(value: any[]) {
    this._locations.set(value || []);
    this.renderRoute();
  }

  @Input() set geoRadius(value: number) {
    if (this._geoRadius !== value) {
      this._geoRadius = value;
      this.renderRadius();
    }
  }

  @Input() allowMapSelection: boolean = false;
  @Input() height: string = 'h-96';
  @Input() selectionModeActiveEnabled: boolean = true;

  @Output() locationSelected = new EventEmitter<{ latitude: number; longitude: number }>();

  _locations = signal<any[]>([]);
  private _geoRadius: number = 100;
  private optimizeRoute = signal(false);
  routeInfo = signal<{ distance: string; duration: string } | null>(null);
  loading = signal(false);
  selectionModeActive = signal(false);
  locationsUpdated = output<any>();
  radiusDataSource: atlas.source.DataSource;

  private map: atlas.Map;
  private datasource: atlas.source.DataSource;
  private locationUpdatedFromSelection = false;
  private routeLine: atlas.Shape;
  private radiusCircle: atlas.Shape;
  private routeService = inject(RouteService);
  private subscription: Subscription;
  private mapClickEventListener: any;
  private radiusCenter: atlas.data.Position;

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.mapClickEventListener) {
      this.map.events.remove('click', this.mapClickEventListener);
    }

    if (this.map) {
      this.map.dispose();
      this.map = null;
    }
  }

  toggleOptimizeRoute(): void {
    this.optimizeRoute.update(value => !value);
    this.renderRoute();
    this.locationsUpdated.emit(this._locations());
  }

  private isPointInRadius(point: atlas.data.Position): boolean {
    if (!this.radiusCenter) return false;

    // Calculate distance between center position and clicked position
    const distance = atlas.math.getDistanceTo(this.radiusCenter, point, 'meters');

    // Return true if distance is less than or equal to radius
    return distance <= this._geoRadius;
  }

  private initMap(): void {
    this.map = new atlas.Map(this.mapId, {
      center: [5.2913, 52.1326], // Nederland centraal
      zoom: 7,
      view: 'Auto',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: '65TAwBgYcORpiWAKdmSK7ek1lv75sxr2-nWJuAGGBb0',
      },
      style: 'grayscale_light',
      // Disable debug and UI elements
      debugMode: false,
      showLogo: false,
      showFeedbackLink: false,
    });

    this.map.events.add('ready', async () => {
      this.datasource = new atlas.source.DataSource();
      this.map.sources.add(this.datasource);

      this.radiusDataSource = new atlas.source.DataSource();
      this.map.sources.add(this.radiusDataSource);

      // Create custom marker icons
      // Wait for all icons to be created before adding layers
      await Promise.all([
        this.map.imageSprite.createFromTemplate('marker-start', 'marker', '#16a34a', '#1a5c4f', 0.8),
        this.map.imageSprite.createFromTemplate('marker-finish', 'marker', '#e74c3c', '#962d22', 0.8),
        this.map.imageSprite.createFromTemplate('marker-route', 'marker', '#38cab3', '#1a5c4f', 0.8),
      ]);

      this.map.layers.add([
        // Radius circle fill layer
        new atlas.layer.PolygonLayer(this.radiusDataSource, null, {
          fillColor: 'rgba(0, 120, 255, 0.2)',
          fillOpacity: 0.5,
        }),

        // Radius circle outline layer
        new atlas.layer.LineLayer(this.radiusDataSource, null, {
          strokeColor: '#38cab3',
          strokeWidth: 2,
          strokeDashArray: [2, 2],
        }),

        // Route lijn laag
        new atlas.layer.LineLayer(this.datasource, null, {
          strokeColor: '#38cab3',
          strokeWidth: 4,
          strokeOpacity: 0.7,
        }),
        // Route items layer - numbered text inside marker
        new atlas.layer.SymbolLayer(this.datasource, null, {
          iconOptions: {
            image: ['coalesce', ['get', 'icon'], 'marker-route'],
            allowOverlap: true,
            ignorePlacement: true,
          },
          textOptions: {
            textField: ['get', 'title'],
            offset: [0, -0.8],
            color: 'white',
            allowOverlap: true,
            ignorePlacement: true,
          },
          filter: ['==', ['get', 'locationType'], 'route-item'],
        }),
        // Start layer - text below marker
        new atlas.layer.SymbolLayer(this.datasource, null, {
          iconOptions: {
            image: ['coalesce', ['get', 'icon'], 'marker-route'],
            allowOverlap: true,
            ignorePlacement: true,
          },
          textOptions: {
            textField: ['get', 'title'],
            offset: [0, 0.75],
            anchor: 'top',
            color: '#333',
            size: 11,
            allowOverlap: true,
            ignorePlacement: true,
          },
          filter: ['==', ['get', 'locationType'], 'departure'],
        }),
        // Finish layer - text below marker (different location than start)
        new atlas.layer.SymbolLayer(this.datasource, null, {
          iconOptions: {
            image: ['coalesce', ['get', 'icon'], 'marker-route'],
            allowOverlap: true,
            ignorePlacement: true,
          },
          textOptions: {
            textField: ['get', 'title'],
            offset: [0, 0.75],
            anchor: 'top',
            color: '#333',
            size: 11,
            allowOverlap: true,
            ignorePlacement: true,
          },
          filter: ['all', ['==', ['get', 'locationType'], 'destination'], ['!=', ['get', 'sameAsStart'], true]],
        }),
        // Finish layer - text further down (same location as start)
        new atlas.layer.SymbolLayer(this.datasource, null, {
          iconOptions: {
            image: ['coalesce', ['get', 'icon'], 'marker-route'],
            allowOverlap: true,
            ignorePlacement: true,
          },
          textOptions: {
            textField: ['get', 'title'],
            offset: [0, 1.5],
            anchor: 'top',
            color: '#333',
            size: 11,
            allowOverlap: true,
            ignorePlacement: true,
          },
          filter: ['all', ['==', ['get', 'locationType'], 'destination'], ['==', ['get', 'sameAsStart'], true]],
        }),
      ]);

      // Add click event handler for location selection
      this.mapClickEventListener = this.map.events.add('click', e => {
        if (this.allowMapSelection && this.selectionModeActive()) {
          // Check if click is within radius
          const clickedPosition = new atlas.data.Position(e.position[0], e.position[1]);

          if (this.isPointInRadius(clickedPosition)) {
            this.locationUpdatedFromSelection = true;

            // Emit the selected location
            this.locationSelected.emit({
              latitude: e.position[1],
              longitude: e.position[0],
            });
          }
        }
      });

      this.renderRoute();
    });
  }

  private renderRadius(): void {
    if (this._geoRadius <= 0 || !this.map || !this.radiusDataSource) {
      if (this.radiusDataSource) {
        this.radiusDataSource.clear();
      }
      return;
    }

    // Check if we have a valid radius center
    if (!this.radiusCenter) {
      return;
    }

    this.radiusDataSource.clear();

    try {
      const radiusInMeters = this._geoRadius;

      // Use the atlas.math utility to generate a circle polygon
      const circleCoords = atlas.math.getRegularPolygonPath(
        this.radiusCenter,
        radiusInMeters,
        36, // Number of points to create a smooth circle
        'meters'
      );

      // Add the circle polygon to the data source
      this.radiusCircle = new atlas.Shape(new atlas.data.Polygon([circleCoords]));
      this.radiusDataSource.add(this.radiusCircle);
      this.selectionModeActive.set(true);
    } catch (error) {
      console.error('Error creating radius circle:', error);
    }
  }

  private renderRoute(): void {
    const locations = this._locations();
    if (!locations.length || !this.map || !this.datasource) {
      return;
    }

    // Clear datasource
    this.datasource.clear();
    this.routeInfo.set(null);

    // First pass: find departure coordinates for comparison
    let departureCoords: number[] | null = null;
    const departureLoc = locations.find((loc: any) => loc.locationType === 'departure');
    if (departureLoc) {
      const depGeo = departureLoc.location?.entry?.geolocation || departureLoc.location?.entry?.address;
      if (depGeo?.latitude && depGeo?.longitude) {
        departureCoords = [parseFloat(depGeo.longitude), parseFloat(depGeo.latitude)];
      }
    }

    // Extract coordinates from locations
    const waypoints = locations
      .map((loc: any, index: number) => {
        let geolocation = loc.location?.entry?.geolocation;

        // Support for different location formats
        if (loc.location?.entry?.address?.latitude != null) {
          geolocation = {
            latitude: loc.location?.entry?.address.latitude,
            longitude: loc.location?.entry?.address.longitude,
          };
        }

        let coordinates;
        if (geolocation?.latitude && geolocation?.longitude) {
          coordinates = [parseFloat(geolocation.longitude), parseFloat(geolocation.latitude)];

          // Set radius center for departure location
          if (loc.locationType === 'departure' && !this.locationUpdatedFromSelection) {
            this.radiusCenter = new atlas.data.Position(coordinates[0], coordinates[1]);
            this.renderRadius();
          }
        } else {
          return;
        }

        // Determine marker style based on location type
        let markerIcon = 'marker-blue';
        let markerLabel = '';
        const locationType = loc.locationType;
        let markerCoordinates = coordinates;

        // Track if destination is same as departure for text offset
        let sameAsStart = false;

        if (locationType === 'departure') {
          // Start location - green marker with "Start" label below
          markerIcon = 'marker-start';
          markerLabel = 'Start';
        } else if (locationType === 'destination') {
          // End location - red marker with "Finish" label below
          markerIcon = 'marker-finish';
          markerLabel = 'Finish';

          // Check if destination is same location as departure
          if (
            departureCoords &&
            Math.abs(coordinates[0] - departureCoords[0]) < 0.0001 &&
            Math.abs(coordinates[1] - departureCoords[1]) < 0.0001
          ) {
            sameAsStart = true;
            // Add small offset (~50m) to make finish marker visible
            markerCoordinates = [coordinates[0] + 0.0005, coordinates[1] + 0.0003];
          }
        } else if (locationType === 'route-item') {
          // Route items - numbered markers starting at 1
          markerIcon = 'marker-route';
          markerLabel = String(loc.routeIndex || index);
        } else {
          // Fallback for unknown types
          markerLabel = String(index + 1);
        }

        // Waypoint marker toevoegen
        this.datasource.add(
          new atlas.data.Feature(new atlas.data.Point(markerCoordinates), {
            title: markerLabel,
            icon: markerIcon,
            locationType: locationType,
            sameAsStart: sameAsStart,
          })
        );

        return coordinates;
      })
      .filter(Boolean); // Filter null/undefined waardes

    this.locationUpdatedFromSelection = false;

    if (waypoints.length === 0) {
      return;
    }

    if (waypoints.length === 1) {
      this.map.setCamera({
        center: waypoints[0],
        zoom: this.map.getCamera().zoom < 13 ? 13 : this.map.getCamera().zoom,
      });
      return;
    }

    // When there are multiple waypoints, calculate a real route
    this.calculateRealRoute(waypoints);
  }

  private calculateRealRoute(waypoints: number[][]): void {
    // Show loading state
    this.loading.set(true);

    // Fetch route via RouteService
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.routeService
      .getRoute({
        waypoints,
        optimize: this.optimizeRoute(),
      })
      .subscribe(response => {
        this.loading.set(false);

        if (!response) {
          this.fallbackToDirectLine(waypoints);
          return;
        }

        // Process the route response
        const routeData = this.routeService.processRouteResponse(response);
        if (!routeData) {
          this.fallbackToDirectLine(waypoints);
          return;
        }

        // Draw the route on the map
        this.routeLine = new atlas.Shape(new atlas.data.LineString(routeData.routePoints));
        this.datasource.add(this.routeLine);

        // Update route information
        this.routeInfo.set({
          distance: routeData.distance.toFixed(1),
          duration: this.routeService.formatDuration(routeData.duration),
        });

        // Handle optimized waypoint order
        // if (this.optimizeRoute() && routeData.optimizedWaypoints && routeData.optimizedWaypoints.length > 0) {
        //   // Create a copy of the current locations array
        //   const currentLocations = [...this._locations()];
        //   const optimizedLocations = [...currentLocations];
        //
        //   // Only update positions that were included in the optimized response
        //   routeData.optimizedWaypoints.forEach(waypoint => {
        //     const providedIndex = waypoint['providedIndex'];
        //     const optimizedIndex = waypoint['optimizedIndex'];
        //
        //     // Move the location from its original position to its optimized position
        //     optimizedLocations[optimizedIndex] = currentLocations[providedIndex];
        //   });
        //
        //   // Update the locations signal with the new order
        //   // this._locations.set(optimizedLocations);
        //
        //   // Emit the updated locations
        //   // this.locationsUpdated.emit(optimizedLocations);
        //   console.log('Optimized locations:', optimizedLocations);
        // }

        // Adjust the map view to show all points
        const bounds = atlas.data.BoundingBox.fromPositions(routeData.routePoints);
        this.map.setCamera({
          bounds: bounds,
          padding: 80,
        });
      });
  }

  private fallbackToDirectLine(waypoints: number[][]): void {
    // Draw a straight line between points as fallback
    this.routeLine = new atlas.Shape(new atlas.data.LineString(waypoints));
    this.datasource.add(this.routeLine);

    // Calculate a simple estimate
    let totalDistance = 0;
    for (let i = 1; i < waypoints.length; i++) {
      const dx = waypoints[i][0] - waypoints[i - 1][0];
      const dy = waypoints[i][1] - waypoints[i - 1][1];
      totalDistance += Math.sqrt(dx * dx + dy * dy) * 111; // Rough estimate: 1 degree ≈ 111 km
    }

    const hours = Math.floor(totalDistance / 80); // Assuming 80 km/h average
    const minutes = Math.floor(((totalDistance % 80) / 80) * 60);

    this.routeInfo.set({
      distance: totalDistance.toFixed(1),
      duration: hours > 0 ? `${hours}u ${minutes}m` : `${minutes}m`,
    });

    // Adjust the map view
    const bounds = atlas.data.BoundingBox.fromPositions(waypoints);
    this.map.setCamera({
      bounds: bounds,
      padding: 80,
    });
  }
}
